import { z } from 'zod';
/**
 * Row-scope qualifiers.
 *
 * The permission matrix cell "Tour Manager -> Booking Management: full (active
 * bookings only)" is NOT a fourth access level. It is a row filter layered on
 * top of FULL. Level and scope are orthogonal columns.
 *
 * SECURITY MODEL: the set of strategies is a fixed whitelist in code; only the
 * CHOICE of strategy and its parameters are data. So the Owner can widen or
 * narrow a scope at runtime without a deploy, but cannot inject a new filter
 * kind or arbitrary SQL.
 *
 * Every failure path in this file DENIES. Parsing is never allowed to fall back
 * to "unfiltered", because unfiltered is the dangerous direction.
 */
/** Field names are restricted to plain identifiers — no relation traversal, no operators. */
const fieldName = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'field must be a simple column identifier');
export const permissionScopeSchema = z
    .discriminatedUnion('type', [
    z.object({ type: z.literal('all') }).strict(),
    z
        .object({
        type: z.literal('fieldIn'),
        field: fieldName,
        // `.min(1)` is deliberate. An empty `values` array is ambiguous — it
        // could read as "match nothing" or "no constraint". Rather than pick,
        // we reject it, and rejection denies. Seeds must be explicit.
        values: z.array(z.string().min(1).max(128)).min(1).max(100),
    })
        .strict(),
    z
        .object({
        type: z.literal('ownedBy'),
        field: fieldName,
    })
        .strict(),
])
    // `.strict()` on each member also rejects `__proto__` / `constructor` keys,
    // so a polluted JSON payload fails parsing instead of reaching a query.
    .brand();
/**
 * Parse an untrusted scope value (a JSON column, editable at runtime).
 *
 * `null`/`undefined` means "no scope recorded", which is the common case and is
 * NOT an error — it resolves to `{ type: 'all' }`, i.e. the level alone governs.
 * Anything present but unparseable is an error and the caller must deny.
 */
export function parseScope(value) {
    if (value === null || value === undefined) {
        return { ok: true, scope: { type: 'all' } };
    }
    let candidate = value;
    if (typeof value === 'string') {
        try {
            candidate = JSON.parse(value);
        }
        catch {
            return { ok: false, reason: 'scope is not valid JSON' };
        }
    }
    const parsed = permissionScopeSchema.safeParse(candidate);
    if (!parsed.success) {
        return { ok: false, reason: parsed.error.issues[0]?.message ?? 'unknown scope shape' };
    }
    return { ok: true, scope: parsed.data };
}
/** True when the scope imposes no row restriction. */
export function isUnscoped(scope) {
    return scope.type === 'all';
}
//# sourceMappingURL=scope.js.map